/**
 * MafiaChat — Day chat + Mafia private night chat
 *
 * 500 char limit, auto-scroll, disabled states per phase.
 *
 * Requirements: 8.1–8.5, 15.1–15.8, 16.1–16.5, 21.2, 21.3
 */

import React, { useState, useRef, useEffect } from 'react';
import { ChatMessage, ChatChannel } from '../types';
import './MafiaChat.css';

export interface MafiaChatProps {
  messages: ChatMessage[];
  onSendMessage: (text: string) => void;
  disabled: boolean;
  channel: ChatChannel;
  playerName: string;
}

const MAX_CHARS = 500;

export const MafiaChat: React.FC<MafiaChatProps> = ({
  messages,
  onSendMessage,
  disabled,
  channel,
  playerName,
}) => {
  const [text, setText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages.length]);

  const handleSend = () => {
    const trimmed = text.trim();
    if (trimmed.length > 0 && trimmed.length <= MAX_CHARS && !disabled) {
      onSendMessage(trimmed);
      setText('');
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const formatTime = (timestamp: number) => {
    const d = new Date(timestamp);
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const isMafiaChannel = channel === 'mafia-night';
  const headerText = isMafiaChannel ? '🔪 Mafia Chat (Private)' : '☀️ Day Chat';

  return (
    <div
      className={`mafia-chat ${isMafiaChannel ? 'mafia-chat--mafia-night' : ''}`}
      role="log"
      aria-label={headerText}
    >
      <div className="mafia-chat__header">{headerText}</div>

      <div className="mafia-chat__messages" aria-live="polite">
        {messages.length === 0 && (
          <div className="mafia-chat__empty">No messages yet</div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className="mafia-chat__message">
            <span className="mafia-chat__message-sender">{msg.senderName}:</span>
            {msg.text}
            <span className="mafia-chat__message-time">{formatTime(msg.timestamp)}</span>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {disabled ? (
        <div className="mafia-chat__disabled-notice">
          Chat is disabled during this phase
        </div>
      ) : (
        <div className="mafia-chat__input-area">
          <input
            className="mafia-chat__input"
            type="text"
            value={text}
            onChange={(e) => setText(e.target.value.slice(0, MAX_CHARS))}
            onKeyDown={handleKeyDown}
            placeholder={`Message as ${playerName}...`}
            disabled={disabled}
            maxLength={MAX_CHARS}
            aria-label="Chat message input"
          />
          <span
            className={`mafia-chat__char-count ${text.length > MAX_CHARS - 50 ? 'mafia-chat__char-count--warning' : ''}`}
          >
            {text.length}/{MAX_CHARS}
          </span>
          <button
            className="mafia-chat__send-btn"
            onClick={handleSend}
            disabled={disabled || text.trim().length === 0}
            aria-label="Send message"
          >
            Send
          </button>
        </div>
      )}
    </div>
  );
};

export default MafiaChat;
